import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, User, Clock } from "lucide-react";

const BlogCarousel = () => {
	const [blogs, setBlogs] = useState([]);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [isAutoPlaying, setIsAutoPlaying] = useState(true);

	// Fetch blogs from blogs.json
	useEffect(() => {
		fetch("/blogs.json")
			.then((res) => res.json())
			.then((data) => {
				setBlogs(data);
			})
			.catch((error) => {
				console.error("Error fetching blogs:", error);
			});
	}, []);

	// Auto-play functionality
	useEffect(() => {
		if (!isAutoPlaying || blogs.length === 0) return;

		const interval = setInterval(() => {
			setCurrentSlide((prev) => (prev + 1) % blogs.length);
		}, 4000);

		return () => clearInterval(interval);
	}, [blogs.length, isAutoPlaying]);

	const formatDate = (date) =>
		new Date(date).toLocaleDateString("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric",
		});

	const nextSlide = () => {
		setCurrentSlide((prev) => (prev + 1) % blogs.length);
	};

	const prevSlide = () => {
		setCurrentSlide((prev) => (prev - 1 + blogs.length) % blogs.length);
	};

	const goToSlide = (index) => {
		setCurrentSlide(index);
	};

	const handleCardClick = (slug) => {
		// Navigate to blog detail page
		window.location.href = `/blog/${slug}`;
	};

	// Get visible slides (previous, current, next)
	const getVisibleSlides = () => {
		if (blogs.length === 0) return [];

		const prevIndex = (currentSlide - 1 + blogs.length) % blogs.length;
		const nextIndex = (currentSlide + 1) % blogs.length;

		return [
			{ ...blogs[prevIndex], position: "prev", index: prevIndex },
			{ ...blogs[currentSlide], position: "current", index: currentSlide },
			{ ...blogs[nextIndex], position: "next", index: nextIndex },
		];
	};

	if (blogs.length === 0) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='text-gray-500'>Loading blogs...</div>
			</div>
		);
	}

	const visibleSlides = getVisibleSlides();

	return (
		// Inside your BlogCarousel component's return statement:

		<div className='relative w-full bg-white py-8 overflow-hidden'>
			<div className='relative w-full max-w-6xl mx-auto px-4'>
				<div className='relative mb-20 lg:mb-24'>
					<div className='relative h-[400px] md:h-[500px]'>
						<div className='relative w-full h-full flex items-center justify-center'>
							{/* Prev Slide - only on large screens */}
							{blogs.length > 1 && (
								<div
									className={`
                hidden lg:block
                absolute w-full z-10 opacity-60 m-10
                transform -translate-x-[75%] scale-90
                transition-all duration-700 ease-in-out
              `}
									style={{ left: 0 }}>
									<div className='relative h-[400px] lg:h-[500px] overflow-hidden group mx-6 cursor-default'>
										<img
											src={
												blogs[(currentSlide - 1 + blogs.length) % blogs.length]
													?.imageUrl
											}
											alt={
												blogs[(currentSlide - 1 + blogs.length) % blogs.length]
													?.title
											}
											className='absolute inset-0 w-full h-full object-cover'
											onError={(e) => {
												e.target.src =
													"https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
											}}
										/>
										<div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent'></div>
									</div>
								</div>
							)}

							{/* Current Slide - always visible */}
							<div
								className={`
              relative w-full z-20 opacity-100
              transform translate-x-0 scale-100
              transition-all duration-700 ease-in-out
              cursor-pointer
            `}
								onClick={() => handleCardClick(blogs[currentSlide]?.slug)}>
								<div className='relative h-[400px] lg:h-[500px] overflow-hidden group mx-6'>
									<img
										src={blogs[currentSlide]?.imageUrl}
										alt={blogs[currentSlide]?.title}
										className='absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700'
										onError={(e) => {
											e.target.src =
												"https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
										}}
									/>
									<div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent'></div>

									{/* Overlay on small/medium devices */}
									<div className='absolute bottom-0 left-0 right-0 bg-white bg-opacity-90 p-4 lg:hidden'>
										{/* Overlay content here (category, title, meta info) */}
										<div className='flex items-center gap-2 mb-2'>
											<div className='p-1.5 bg-blue-500 rounded-full'>
												<MapPin className='w-3 h-3 text-white' />
											</div>
											<span className='text-sm font-medium text-gray-600 capitalize'>
												{blogs[currentSlide]?.tags?.[0] || "Health"}
											</span>
										</div>
										<h2 className='text-lg font-bold text-gray-900 leading-tight mb-2'>
											{blogs[currentSlide]?.title}
										</h2>
										<div className='flex items-center gap-4 text-sm text-gray-600 flex-wrap'>
											<div className='flex items-center gap-2'>
												<div className='w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center'>
													<User className='w-3.5 h-3.5 text-gray-600' />
												</div>
												<span className='font-medium'>Healthians Team</span>
											</div>
											<span className='text-gray-400'>•</span>
											<span>{formatDate(blogs[currentSlide]?.date)}</span>
											<span className='text-gray-400'>•</span>
											<div className='flex items-center gap-1'>
												<div className='w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center'>
													<Clock className='w-2.5 h-2.5 text-white' />
												</div>
												<span>9 Min Read</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Next Slide - only on large screens */}
							{blogs.length > 1 && (
								<div
									className={`
                hidden lg:block
                absolute w-full z-10 opacity-60
                transform translate-x-[75%] scale-90
                transition-all duration-700 ease-in-out
              `}
									style={{ right: 0 }}>
									<div className='relative h-[400px] lg:h-[500px] overflow-hidden group mx-6 cursor-default'>
										<img
											src={blogs[(currentSlide + 1) % blogs.length]?.imageUrl}
											alt={blogs[(currentSlide + 1) % blogs.length]?.title}
											className='absolute inset-0 w-full h-full object-cover'
											onError={(e) => {
												e.target.src =
													"https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
											}}
										/>
										<div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent'></div>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Overlay Card - only on large screens */}
					<div className='hidden lg:flex absolute -bottom-16 justify-center lg:-bottom-20 left-1/2 transform -translate-x-1/2 w-[85%] lg:w-[75%] max-w-4xl z-30'>
						<div
							className='bg-white flex items-center justify-center flex-col shadow-2xl p-6 lg:p-8 cursor-pointer transition-all duration-300 hover:shadow-3xl hover:-translate-y-1'
							onClick={() => handleCardClick(blogs[currentSlide]?.slug)}
							style={{
								boxShadow:
									"0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
							}}>
							{/* Category with Icon */}
							<div className='flex items-center gap-2 mb-4'>
								<div className='p-1.5 bg-blue-500 rounded-full'>
									<MapPin className='w-3 h-3 text-white' />
								</div>
								<span className='text-sm font-medium text-gray-600 capitalize'>
									{blogs[currentSlide]?.tags?.[0] || "Health"}
								</span>
							</div>
							{/* Title */}
							<h2 className='text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 leading-tight mb-4 text-center'>
								{blogs[currentSlide]?.title}
							</h2>
							{/* Meta Information */}
							<div className='flex items-center gap-4 text-sm text-gray-600 flex-wrap justify-center'>
								{/* Author */}
								<div className='flex items-center gap-2'>
									<div className='w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center'>
										<User className='w-3.5 h-3.5 text-gray-600' />
									</div>
									<span className='font-medium'>Fine Labs Team</span>
								</div>
								<span className='text-gray-400'>•</span>
								<span>{formatDate(blogs[currentSlide]?.date)}</span>
								<span className='text-gray-400'>•</span>
								<div className='flex items-center gap-1'>
									<div className='w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center'>
										<Clock className='w-2.5 h-2.5 text-white' />
									</div>
									<span>9 Min Read</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Navigation Arrows - only on large screens */}
				<button
					onClick={prevSlide}
					className='hidden lg:flex absolute left-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full items-center justify-center text-white shadow-lg transition-all duration-300 hover:scale-110 z-40'
					style={{ marginTop: "-60px" }}>
					<ChevronLeft className='w-6 h-6' />
				</button>
				<button
					onClick={nextSlide}
					className='hidden lg:flex absolute right-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full items-center justify-center text-white shadow-lg transition-all duration-300 hover:scale-110 z-40'
					style={{ marginTop: "-60px" }}>
					<ChevronRight className='w-6 h-6' />
				</button>
			</div>

			{/* Dots Indicator */}
			<div className='flex justify-center mt-8 gap-2'>
				{blogs.map((_, index) => (
					<button
						key={index}
						onClick={() => goToSlide(index)}
						className={`w-2 h-2 rounded-full transition-all duration-300 ${
							index === currentSlide
								? "bg-blue-500 scale-125"
								: "bg-gray-300 hover:bg-gray-400"
						}`}
					/>
				))}
			</div>
		</div>
	);
};

export default BlogCarousel;

// import React, { useEffect, useState } from "react";
// import { ChevronLeft, ChevronRight, MapPin, User, Clock } from "lucide-react";

// const BlogCarousel = () => {
// 	const [blogs, setBlogs] = useState([]);
// 	const [currentSlide, setCurrentSlide] = useState(0);
// 	const [isAutoPlaying, setIsAutoPlaying] = useState(true);
// 	const [direction, setDirection] = useState("right"); // "left" or "right"

// 	// Fetch blogs from blogs.json
// 	useEffect(() => {
// 		fetch("/blogs.json")
// 			.then((res) => res.json())
// 			.then((data) => {
// 				setBlogs(data);
// 			})
// 			.catch((error) => {
// 				console.error("Error fetching blogs:", error);
// 			});
// 	}, []);

// 	// Auto-play functionality
// 	useEffect(() => {
// 		if (!isAutoPlaying || blogs.length === 0) return;

// 		const interval = setInterval(() => {
// 			setDirection("right");
// 			setCurrentSlide((prev) => (prev + 1) % blogs.length);
// 		}, 4000);

// 		return () => clearInterval(interval);
// 	}, [blogs.length, isAutoPlaying]);

// 	const formatDate = (date) =>
// 		new Date(date).toLocaleDateString("en-US", {
// 			month: "long",
// 			day: "numeric",
// 			year: "numeric",
// 		});

// 	const nextSlide = () => {
// 		setDirection("right");
// 		setCurrentSlide((prev) => (prev + 1) % blogs.length);
// 	};

// 	const prevSlide = () => {
// 		setDirection("left");
// 		setCurrentSlide((prev) => (prev - 1 + blogs.length) % blogs.length);
// 	};

// 	const goToSlide = (index) => {
// 		setDirection(index > currentSlide ? "right" : "left");
// 		setCurrentSlide(index);
// 	};

// 	const handleCardClick = (slug) => {
// 		window.location.href = `/blog/${slug}`;
// 	};

// 	// Get visible slides (previous, current, next)
// 	const getVisibleSlides = () => {
// 		if (blogs.length === 0) return [];

// 		const prevIndex = (currentSlide - 1 + blogs.length) % blogs.length;
// 		const nextIndex = (currentSlide + 1) % blogs.length;

// 		return [
// 			{ ...blogs[prevIndex], position: "prev", index: prevIndex },
// 			{ ...blogs[currentSlide], position: "current", index: currentSlide },
// 			{ ...blogs[nextIndex], position: "next", index: nextIndex },
// 		];
// 	};

// 	if (blogs.length === 0) {
// 		return (
// 			<div className='flex justify-center items-center h-64'>
// 				<div className='text-gray-500'>Loading blogs...</div>
// 			</div>
// 		);
// 	}

// 	const visibleSlides = getVisibleSlides();

// 	return (
// 		<div className='relative w-full bg-white py-8 overflow-hidden'>
// 			<div className='relative w-full max-w-6xl mx-auto px-4'>
// 				<div className='relative mb-20 lg:mb-24'>
// 					<div className='relative h-[400px] md:h-[500px]'>
// 						<div className='relative w-full h-full flex items-center justify-center overflow-hidden'>
// 							{/* Prev Slide - only on large screens */}
// 							{blogs.length > 1 && (
// 								<div
// 									className={`
//                 hidden lg:block
//                 absolute w-full z-10 opacity-60 m-10
//                 transform -translate-x-[75%] scale-90
//                 transition-all duration-700 ease-in-out
//               `}
// 									style={{ left: 0 }}>
// 									<div className='relative h-[400px] lg:h-[500px] overflow-hidden group mx-6 cursor-default'>
// 										<img
// 											src={
// 												blogs[(currentSlide - 1 + blogs.length) % blogs.length]
// 													?.imageUrl
// 											}
// 											alt={
// 												blogs[(currentSlide - 1 + blogs.length) % blogs.length]
// 													?.title
// 											}
// 											className='absolute inset-0 w-full h-full object-cover'
// 											onError={(e) => {
// 												e.target.src =
// 													"https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
// 											}}
// 										/>
// 										<div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent'></div>
// 									</div>
// 								</div>
// 							)}

// 							{/* Current Slide - always visible, with directional animation */}
// 							<div
// 								key={blogs[currentSlide]?.slug}
// 								className={`
//                   relative w-full z-20 opacity-100
//                   transform translate-x-0 scale-100
//                   transition-all duration-700 ease-in-out
//                   cursor-pointer
//                   ${direction === "right" ? "slide-in-right" : "slide-in-left"}
//                 `}
// 								onClick={() => handleCardClick(blogs[currentSlide]?.slug)}>
// 								<div className='relative h-[400px] lg:h-[500px] overflow-hidden group mx-6'>
// 									<img
// 										src={blogs[currentSlide]?.imageUrl}
// 										alt={blogs[currentSlide]?.title}
// 										className='absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700'
// 										onError={(e) => {
// 											e.target.src =
// 												"https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
// 										}}
// 									/>
// 									<div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent'></div>

// 									{/* Overlay on small/medium devices */}
// 									<div className='absolute bottom-0 left-0 right-0 bg-white bg-opacity-90 p-4 lg:hidden'>
// 										<div className='flex items-center gap-2 mb-2'>
// 											<div className='p-1.5 bg-blue-500 rounded-full'>
// 												<MapPin className='w-3 h-3 text-white' />
// 											</div>
// 											<span className='text-sm font-medium text-gray-600 capitalize'>
// 												{blogs[currentSlide]?.tags?.[0] || "Health"}
// 											</span>
// 										</div>
// 										<h2 className='text-lg font-bold text-gray-900 leading-tight mb-2'>
// 											{blogs[currentSlide]?.title}
// 										</h2>
// 										<div className='flex items-center gap-4 text-sm text-gray-600 flex-wrap'>
// 											<div className='flex items-center gap-2'>
// 												<div className='w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center'>
// 													<User className='w-3.5 h-3.5 text-gray-600' />
// 												</div>
// 												<span className='font-medium'>Healthians Team</span>
// 											</div>
// 											<span className='text-gray-400'>•</span>
// 											<span>{formatDate(blogs[currentSlide]?.date)}</span>
// 											<span className='text-gray-400'>•</span>
// 											<div className='flex items-center gap-1'>
// 												<div className='w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center'>
// 													<Clock className='w-2.5 h-2.5 text-white' />
// 												</div>
// 												<span>9 Min Read</span>
// 											</div>
// 										</div>
// 									</div>
// 								</div>
// 							</div>

// 							{/* Next Slide - only on large screens */}
// 							{blogs.length > 1 && (
// 								<div
// 									className={`
//                     hidden lg:block
//                     absolute w-full z-10 opacity-60
//                     transform translate-x-[75%] scale-90
//                     transition-all duration-700 ease-in-out
//                   `}
// 									style={{ right: 0 }}>
// 									<div className='relative h-[400px] lg:h-[500px] overflow-hidden group mx-6 cursor-default'>
// 										<img
// 											src={blogs[(currentSlide + 1) % blogs.length]?.imageUrl}
// 											alt={blogs[(currentSlide + 1) % blogs.length]?.title}
// 											className='absolute inset-0 w-full h-full object-cover'
// 											onError={(e) => {
// 												e.target.src =
// 													"https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
// 											}}
// 										/>
// 										<div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent'></div>
// 									</div>
// 								</div>
// 							)}
// 						</div>
// 					</div>

// 					{/* Overlay Card - only on large screens */}
// 					<div className='hidden lg:flex absolute -bottom-16 justify-center lg:-bottom-20 left-1/2 transform -translate-x-1/2 w-[85%] lg:w-[75%] max-w-4xl z-30'>
// 						<div
// 							className='bg-white flex items-center justify-center flex-col shadow-2xl p-6 lg:p-8 cursor-pointer transition-all duration-300 hover:shadow-3xl hover:-translate-y-1'
// 							onClick={() => handleCardClick(blogs[currentSlide]?.slug)}
// 							style={{
// 								boxShadow:
// 									"0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
// 							}}>
// 							<div className='flex items-center gap-2 mb-4'>
// 								<div className='p-1.5 bg-blue-500 rounded-full'>
// 									<MapPin className='w-3 h-3 text-white' />
// 								</div>
// 								<span className='text-sm font-medium text-gray-600 capitalize'>
// 									{blogs[currentSlide]?.tags?.[0] || "Health"}
// 								</span>
// 							</div>
// 							<h2 className='text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 leading-tight mb-4 text-center'>
// 								{blogs[currentSlide]?.title}
// 							</h2>
// 							<div className='flex items-center gap-4 text-sm text-gray-600 flex-wrap justify-center'>
// 								<div className='flex items-center gap-2'>
// 									<div className='w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center'>
// 										<User className='w-3.5 h-3.5 text-gray-600' />
// 									</div>
// 									<span className='font-medium'>Healthians Team</span>
// 								</div>
// 								<span className='text-gray-400'>•</span>
// 								<span>{formatDate(blogs[currentSlide]?.date)}</span>
// 								<span className='text-gray-400'>•</span>
// 								<div className='flex items-center gap-1'>
// 									<div className='w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center'>
// 										<Clock className='w-2.5 h-2.5 text-white' />
// 									</div>
// 									<span>9 Min Read</span>
// 								</div>
// 							</div>
// 						</div>
// 					</div>
// 				</div>

// 				{/* Navigation Arrows - only on large screens */}
// 				<button
// 					onClick={prevSlide}
// 					className='hidden lg:flex absolute left-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full items-center justify-center text-white shadow-lg transition-all duration-300 hover:scale-110 z-40'
// 					style={{ marginTop: "-60px" }}>
// 					<ChevronLeft className='w-6 h-6' />
// 				</button>
// 				<button
// 					onClick={nextSlide}
// 					className='hidden lg:flex absolute right-0 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full items-center justify-center text-white shadow-lg transition-all duration-300 hover:scale-110 z-40'
// 					style={{ marginTop: "-60px" }}>
// 					<ChevronRight className='w-6 h-6' />
// 				</button>
// 			</div>

// 			{/* Dots Indicator */}
// 			<div className='flex justify-center mt-8 gap-2'>
// 				{blogs.map((_, index) => (
// 					<button
// 						key={index}
// 						onClick={() => goToSlide(index)}
// 						className={`w-2 h-2 rounded-full transition-all duration-300 ${
// 							index === currentSlide
// 								? "bg-blue-500 scale-125"
// 								: "bg-gray-300 hover:bg-gray-400"
// 						}`}
// 					/>
// 				))}
// 			</div>
// 		</div>
// 	);
// };

// export default BlogCarousel;
